import React, { useEffect, useMemo, useState } from "react";
import TopBar from "../../components/TopBar";

async function fetchJSON(url, options) {
  const res = await fetch(url, options);
  const text = await res.text();

  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    const preview = text ? text.slice(0, 180) : "(vuoto)";
    throw new Error(`Risposta non valida dal server (${res.status}). Anteprima: ${preview}`);
  }

  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || `Errore HTTP ${res.status}`;
    throw new Error(msg);
  }

  return data;
}

function toSQLDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toSQLTime(d) {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function labelFromCaso(c) {
  if (c === "caso1") return "Caso 1 (Conforme)";
  if (c === "caso2") return "Caso 2 (Tendenza al degrado)";
  if (c === "caso3") return "Caso 3 (Non conforme)";
  return c || "—";
}

function labelFromAzione(a) {
  if (a === "nessuna") return "Nessuna azione";
  if (a === "filtraggio") return "Filtraggio olio";
  if (a === "cambio") return "Cambio olio";
  return a || "—";
}

function badgeCasoClass(c) {
  if (c === "caso1") return "bg-emerald-100 text-emerald-700";
  if (c === "caso2") return "bg-amber-100 text-amber-700";
  if (c === "caso3") return "bg-rose-100 text-rose-700";
  return "bg-slate-100 text-slate-700";
}

function badgeAzioneClass(a) {
  if (a === "nessuna") return "bg-slate-100 text-slate-700";
  if (a === "filtraggio") return "bg-amber-100 text-amber-700";
  if (a === "cambio") return "bg-blue-100 text-blue-700";
  return "bg-slate-100 text-slate-700";
}

function normalizeErr(e) {
  const msg = e?.message || "Errore imprevisto";
  if (/Non autenticato/i.test(msg) || /401/.test(msg)) {
    return "Sessione scaduta o non autenticato. Esegui il login e riprova.";
  }
  return msg;
}

export default function CambioOlio({ imageUrl, onLogout = () => { } }) {
  const [selectedCase, setSelectedCase] = useState(null);
  const [pendingCase, setPendingCase] = useState(null);
  const [actionPanelOpen, setActionPanelOpen] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [fryers, setFryers] = useState([]);
  const [loadingFryers, setLoadingFryers] = useState(true);
  const [selectedFryerId, setSelectedFryerId] = useState("");

  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);

  const [userInfo, setUserInfo] = useState(null);
  const [testPhoto, setTestPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  const cases = useMemo(
    () => [
      {
        key: "caso1",
        title: "Caso 1",
        desc: "Olio conforme",
        badge: "OK",
        tone: "border-emerald-300 bg-emerald-50",
      },
      {
        key: "caso2",
        title: "Caso 2",
        desc: "Tendenza al degrado",
        badge: "Intervenire",
        tone: "border-amber-300 bg-amber-50",
      },
      {
        key: "caso3",
        title: "Caso 3",
        desc: "Non conforme",
        badge: "Cambiare",
        tone: "border-rose-300 bg-rose-50",
      },
    ],
    []
  );

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/me.php", { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        setUserInfo(data);
      } catch {
        // non blocchiamo la pagina
      }
    })();
  }, []);

  useEffect(() => {
    loadFryers();
  }, []);

  useEffect(() => {
    if (!testPhoto) {
      setPhotoPreview("");
      return undefined;
    }

    const url = URL.createObjectURL(testPhoto);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [testPhoto]);

  useEffect(() => {
    if (selectedFryerId) {
      loadRecent(selectedFryerId);
    } else {
      setRecent([]);
    }
  }, [selectedFryerId]);

  async function loadFryers() {
    try {
      setLoadingFryers(true);
      setError(null);

      const js = await fetchJSON("/api/friggitrici_list.php", {
        credentials: "include",
      });

      const rows = Array.isArray(js?.records) ? js.records : [];
      setFryers(rows);

      if (rows.length > 0) {
        setSelectedFryerId(rows[0].codice);
      }
    } catch (e) {
      setError(normalizeErr(e));
    } finally {
      setLoadingFryers(false);
    }
  }

  async function loadRecent(fryerId) {
    try {
      setLoadingRecent(true);
      setError(null);

      const qs = `?friggitrice=${encodeURIComponent(fryerId)}&limit=10`;
      const js = await fetchJSON(`/api/cambioolio_list.php${qs}`, {
        credentials: "include",
      });

      if (!js?.success) throw new Error(js?.error || "Impossibile leggere lo storico");
      setRecent(Array.isArray(js.records) ? js.records : []);
    } catch (e) {
      setError(normalizeErr(e));
    } finally {
      setLoadingRecent(false);
    }
  }

  function handleSelect(caseKey) {
    if (!selectedFryerId) {
      setError("Seleziona prima una friggitrice.");
      return;
    }

    setSelectedCase(caseKey);
    setPendingCase(caseKey);
    setError(null);

    if (caseKey === "caso1") {
      submitRecord({
        scelta: caseKey,
        azione: "nessuna",
        confermato: 1,
      });
      return;
    }

    setActionPanelOpen(true);
  }

  function handlePhotoChange(event) {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError("Formato foto non supportato. Usa JPG, PNG o WebP.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("La foto non può superare 8 MB.");
      return;
    }

    setTestPhoto(file);
    setError(null);
  }

  async function submitRecord({ scelta, azione, confermato }) {
    try {
      if (!selectedFryerId) throw new Error("Seleziona una friggitrice.");

      setSubmitting(true);
      setError(null);

      const now = new Date();

      const payload = new FormData();
      payload.append("fryerId", selectedFryerId);
      payload.append("scelta", scelta);
      payload.append("azione", azione);
      payload.append("data", toSQLDate(now));
      payload.append("ora", toSQLTime(now));
      payload.append("confermato", confermato ? "1" : "0");
      payload.append("note", "");
      if (testPhoto) payload.append("test_photo", testPhoto);

      const js = await fetchJSON("/api/cambioolio.php", {
        method: "POST",
        credentials: "include",
        body: payload,
      });

      if (!js?.success) {
        throw new Error(js?.error || "Impossibile salvare la registrazione");
      }

      await loadRecent(selectedFryerId);
      setActionPanelOpen(false);
      setPendingCase(null);
      setTestPhoto(null);
    } catch (e) {
      setError(normalizeErr(e));
    } finally {
      setSubmitting(false);
    }
  }

  const userLabel = userInfo?.nome || userInfo?.ruolo || "Utente";
  const userEmail = userInfo?.email || "";

  return (
    <>
      <TopBar
        title="NORMA HACCP — Verifica Olii Frittura"
        onLogout={onLogout}
        user={{
          name: userLabel,
          email: userEmail,
        }}
      />

      <div className="min-h-screen w-full bg-slate-50">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <div className="space-y-6">
            <header className="rounded-3xl border bg-white px-6 py-5 shadow-sm">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                Verifica olii frittura
              </h1>
              <p className="mt-2 text-sm md:text-base text-slate-600 max-w-4xl">
                Seleziona la friggitrice e registra il controllo giornaliero. Per il{" "}
                <strong>Caso 2</strong> puoi scegliere <strong>filtraggio</strong> o{" "}
                <strong>cambio olio</strong>. Per il <strong>Caso 3</strong> viene registrato il
                cambio olio.
              </p>
            </header>

            {imageUrl && (
              <section className="rounded-3xl overflow-hidden border bg-white shadow-sm">
                <img
                  src={imageUrl}
                  alt="Schema casi olio friggitrice"
                  className="w-full max-h-[520px] object-contain bg-white"
                />
              </section>
            )}

            <section className="rounded-3xl border bg-white p-5 shadow-sm">
              <div className="mb-3 font-semibold text-slate-800">Seleziona friggitrice</div>

              {loadingFryers ? (
                <div className="text-sm text-slate-500 italic">Caricamento friggitrici…</div>
              ) : fryers.length === 0 ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-700">
                  Nessuna friggitrice configurata per questa azienda.
                </div>
              ) : (
                <select
                  value={selectedFryerId}
                  onChange={(e) => setSelectedFryerId(e.target.value)}
                  className="w-full md:w-[460px] rounded-2xl border border-slate-300 px-4 py-3 bg-white outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {fryers.map((f) => (
                    <option key={f.id} value={f.codice}>
                      {f.nome} ({f.codice})
                    </option>
                  ))}
                </select>
              )}
            </section>

            <section className="rounded-3xl border bg-white p-5 shadow-sm">
              <div className="mb-4">
                <div className="text-lg font-semibold text-slate-900">Foto evidenza</div>
                <div className="text-sm text-slate-600">
                  Scatta o carica una foto del test dell’olio. Formati JPG, PNG o WebP, massimo 8 MB.
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-start gap-4">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Anteprima evidenza test olio"
                    className="h-36 w-36 rounded-2xl border object-cover"
                  />
                ) : (
                  <div className="flex h-36 w-36 items-center justify-center rounded-2xl border border-dashed bg-slate-50 px-3 text-center text-sm text-slate-500">
                    Nessuna foto selezionata
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  <label className="cursor-pointer rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700">
                    Scatta o carica foto
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      capture="environment"
                      className="hidden"
                      onChange={handlePhotoChange}
                      disabled={submitting}
                    />
                  </label>
                  {testPhoto && (
                    <button
                      type="button"
                      className="rounded-2xl border px-4 py-2.5 text-sm hover:bg-slate-50"
                      onClick={() => setTestPhoto(null)}
                      disabled={submitting}
                    >
                      Rimuovi foto
                    </button>
                  )}
                </div>
              </div>
            </section>

            <section className="rounded-3xl border bg-white p-5 shadow-sm">
              <div className="mb-4">
                <div className="text-lg font-semibold text-slate-900">Controllo odierno</div>
                <div className="text-sm text-slate-600">
                  Scegli il caso riscontrato per l’olio della friggitrice selezionata.
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                {cases.map((c) => {
                  const active = selectedCase === c.key;

                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => handleSelect(c.key)}
                      disabled={!selectedFryerId || submitting}
                      className={[
                        "text-left p-6 rounded-3xl border transition shadow-sm disabled:opacity-50",
                        c.tone,
                        active ? "ring-2 ring-blue-500" : "hover:shadow-md",
                      ].join(" ")}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm text-slate-500 mb-1">{c.title}</div>
                          <div className="text-xl font-semibold text-slate-900">{c.desc}</div>
                        </div>
                        <span className="inline-block rounded-xl bg-white border px-3 py-1 text-xs font-medium">
                          {c.badge}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {actionPanelOpen && (
              <section className="rounded-3xl border bg-white p-5 shadow-sm">
                <h2 className="text-lg font-semibold mb-2 text-slate-900">Intervento sull’olio</h2>

                <p className="text-sm text-slate-600 mb-4">
                  Hai selezionato <strong>{labelFromCaso(pendingCase)}</strong> per la friggitrice{" "}
                  <strong>{selectedFryerId}</strong>.
                </p>

                {pendingCase === "caso2" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      className="rounded-3xl border border-amber-300 bg-amber-50 px-5 py-5 text-left hover:bg-amber-100"
                      onClick={() =>
                        submitRecord({
                          scelta: pendingCase,
                          azione: "filtraggio",
                          confermato: 1,
                        })
                      }
                      disabled={submitting}
                    >
                      <div className="font-semibold text-slate-900 text-base">Filtraggio olio</div>
                      <div className="text-sm text-slate-600 mt-1">
                        Registra un intervento di filtrazione senza sostituzione completa.
                      </div>
                    </button>

                    <button
                      type="button"
                      className="rounded-3xl border border-blue-300 bg-blue-50 px-5 py-5 text-left hover:bg-blue-100"
                      onClick={() =>
                        submitRecord({
                          scelta: pendingCase,
                          azione: "cambio",
                          confermato: 1,
                        })
                      }
                      disabled={submitting}
                    >
                      <div className="font-semibold text-slate-900 text-base">Cambio olio</div>
                      <div className="text-sm text-slate-600 mt-1">
                        Registra la sostituzione completa dell’olio.
                      </div>
                    </button>
                  </div>
                ) : (
                  <div className="mb-4">
                    <div className="rounded-3xl border border-rose-200 bg-rose-50 p-4 mb-3">
                      <div className="font-semibold text-rose-800">Caso 3: olio non conforme</div>
                      <div className="text-sm text-rose-700">
                        Per questo caso viene registrato il cambio olio.
                      </div>
                    </div>

                    <button
                      type="button"
                      className="w-full rounded-3xl border border-blue-300 bg-blue-50 px-5 py-5 text-left hover:bg-blue-100"
                      onClick={() =>
                        submitRecord({
                          scelta: pendingCase,
                          azione: "cambio",
                          confermato: 1,
                        })
                      }
                      disabled={submitting}
                    >
                      <div className="font-semibold text-slate-900 text-base">
                        Conferma cambio olio
                      </div>
                      <div className="text-sm text-slate-600 mt-1">
                        Registra la sostituzione completa dell’olio.
                      </div>
                    </button>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-2xl border bg-white hover:bg-slate-50"
                    onClick={() => {
                      setActionPanelOpen(false);
                      setPendingCase(null);
                    }}
                    disabled={submitting}
                  >
                    Annulla
                  </button>
                </div>
              </section>
            )}

            <section className="rounded-3xl border bg-white overflow-hidden shadow-sm">
              <div className="p-5 border-b bg-slate-50/70">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-lg font-semibold text-slate-900">
                      Registro ultimi 10 controlli
                    </div>
                    <div className="text-sm text-slate-600">
                      Storico della friggitrice selezionata
                      {selectedFryerId ? <strong> — {selectedFryerId}</strong> : null}
                    </div>
                  </div>

                  <a
                    href={
                      selectedFryerId
                        ? `/verifica-olio.php?friggitrice=${encodeURIComponent(selectedFryerId)}`
                        : "/verifica-olio.php"
                    }
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Vedi registro completo
                  </a>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-3 border-b">Data</th>
                      <th className="text-left px-4 py-3 border-b">Ora</th>
                      <th className="text-left px-4 py-3 border-b">Friggitrice</th>
                      <th className="text-left px-4 py-3 border-b">Caso</th>
                      <th className="text-left px-4 py-3 border-b">Azione</th>
                      <th className="text-left px-4 py-3 border-b">Evidenza</th>
                      <th className="text-left px-4 py-3 border-b">Confermato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingRecent ? (
                      <tr>
                        <td className="px-4 py-4 border-b text-slate-500 italic" colSpan={7}>
                          Caricamento storico…
                        </td>
                      </tr>
                    ) : recent.length === 0 ? (
                      <tr>
                        <td className="px-4 py-4 border-b text-slate-500 italic" colSpan={7}>
                          Nessuna registrazione trovata.
                        </td>
                      </tr>
                    ) : (
                      recent.map((r) => (
                        <tr key={r.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 border-b">{r.data}</td>
                          <td className="px-4 py-3 border-b">{r.ora}</td>
                          <td className="px-4 py-3 border-b">
                            {r.friggitrice_nome || r.fryer_id || "—"}
                          </td>
                          <td className="px-4 py-3 border-b">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${badgeCasoClass(
                                r.scelta
                              )}`}
                            >
                              {labelFromCaso(r.scelta)}
                            </span>
                          </td>
                          <td className="px-4 py-3 border-b">
                            {r.test_photo_url ? (
                              <a href={r.test_photo_url} target="_blank" rel="noreferrer">
                                <img
                                  src={r.test_photo_url}
                                  alt="Evidenza test olio"
                                  className="h-12 w-12 rounded-lg border object-cover"
                                />
                              </a>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 border-b">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${badgeAzioneClass(
                                r.azione
                              )}`}
                            >
                              {labelFromAzione(r.azione)}
                            </span>
                          </td>
                          <td className="px-4 py-3 border-b">
                            {Number(r.confermato) ? "Sì" : "No"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {error && (
              <div className="rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
